import { Chart } from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import {
  colors,
  createDiagonalPattern,
  defaultXAxisGridConfig,
  legendLabelsConfig,
  numberToCurrency,
} from './utils'

export const createAnnualCashflowsChart = (graphData, scope) => {
  const chartCanvas = document.getElementById('annualCashFlowsChartCanvas');

  if (!chartCanvas || !graphData[0]) {
    console.log('no data returned or canvas found');
    return;
  }

  chartCanvas.parentElement.style.display = '';

  const chartLoader = document.getElementById('annualChartLoader');
  chartLoader.classList.remove('lds-dual-ring');

  if (!graphData[0].annualData) {
    console.log('no annual cash flow yet for this fund');
    chartCanvas.parentElement.style.display = 'none';
    return;
  }

  const sortedCashflows = graphData[0].annualData.sort((a, b) => a.year - b.year);
  if (!hasNonZeroDataForThisScope(sortedCashflows, scope)) {
    chartCanvas.parentElement.style.display = 'none';
    return;
  }

  const tooltipBody = (item) => {
    const {
      dataset: { label },
      parsed: { y: rawValue },
    } = item;

    return ` ${label} : ${numberToCurrency(rawValue, scope)}`;
  };

  const minYAxisValue = (() => {
    const { annualData } = graphData[0];
    const minCall = Math.min(
      ...annualData.map(
        (y) => y.real[scope].called + y.provisional[scope].called
      )
    );

    return Math.ceil(minCall * 1.6);
  })();

  const maxYAxisValue = (() => {
    const { annualData } = graphData[0];
    const maxCall = Math.max(
      ...annualData.map(
        (y) => y.real[scope].distributed + y.provisional[scope].distributed
      )
    );

    return Math.ceil(maxCall * 1.6);
  })();

  let responsiveRatio = 1.3;

  switch (true) {
    case (window.innerWidth > 576 && window.innerWidth < 1200):
      responsiveRatio = 2;
      break;
    case window.innerWidth >= 1200:
      responsiveRatio = 2.5;
      break;
    default:
      responsiveRatio = 1.3;
  }

  const buildDatasets = (() => {
    let numberOfYears = graphData[0].annualData.length;
    let lastcumulativeDistributedWaitingToBePaid = graphData[0]
      .annualData[numberOfYears - 1]
      .real
      .individual
      .cumulativeDistributedWaitingToBePaid;

    if (scope === 'individual' && lastcumulativeDistributedWaitingToBePaid > 0) {
      return [
        {
          label: 'Appels',
          stack: 'callings',
          order: 1,
          backgroundColor: colors.calls,
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `real.${scope}.called`,
          },
          datalabels: {
            align: 'start',
            anchor: 'start',
            offset: 20,
            display: false,
          },
        },
        {
          label: 'Appels prévisionnels',
          stack: 'callings',
          order: 4,
          backgroundColor: createDiagonalPattern(colors.calls),
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `provisional.${scope}.called`,
          },
          datalabels: {
            align: 'start',
            anchor: 'start',
            offset: 20,
            formatter: function (value) {
              const { called: realCalledAmount } = value.real[scope];
              const { called: provisionalCalledAmount } = value.provisional[scope];

              return numberToCurrency(+realCalledAmount + +provisionalCalledAmount,
                                      scope);
            },
            display: function (context) {
              const value = context.dataset.data[context.dataIndex];
              const { called: realAmount } = value.real[scope];
              const { called: provisionalAmount } = value.provisional[scope];
              const dataDisplayed = !context?.chart?.legend?.legendItems[0]?.hidden;

              return dataDisplayed && +realAmount + +provisionalAmount < 0;
            },
          },
        },
        {
          label: 'Distributions',
          stack: 'distributions',
          order: 2,
          backgroundColor: colors.distributions,
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `real.${scope}.distributed`,
          },
          datalabels: {
            align: 'end',
            anchor: 'end',
            display: function (context) {
              return false;
            },
          },
        },
        {
          label: 'Distributions en attente de versement',
          stack: 'distributions',
          order: 3,
          backgroundColor: colors.distributionsWaitingToBePaid,
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `real.${scope}.distributedWaitingToBePaid`,
          },
          datalabels: {
            align: 'end',
            anchor: 'end',
            display: function (context) {
              return false;
            },
          },
        },
        {
          label: 'Distributions prévisionnelles',
          stack: 'distributions',
          order: 5,
          backgroundColor: createDiagonalPattern(colors.distributions),
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `provisional.${scope}.distributed`,
          },
          datalabels: {
            align: 'end',
            anchor: 'end',
            formatter: function (value) {
              const { distributed: realDistributedAmount } = value.real[scope];
              const { distributed: provisionalDistributedAmount } = value.provisional[scope];

              return numberToCurrency(+realDistributedAmount + +provisionalDistributedAmount,
                                      scope);
            },
            display: function (context) {
              const value = context.dataset.data[context.dataIndex];
              const { distributed: realAmount } = value.real[scope];
              const { distributed: provisionalAmount } = value.provisional[scope];

              const dataDisplayed = !context?.chart?.legend?.legendItems[1]?.hidden;

              return dataDisplayed && +realAmount + +provisionalAmount > 0;
            },
          },
        }
      ]
    } else {
      return [
        {
          label: 'Appels',
          stack: 'callings',
          order: 1,
          backgroundColor: colors.calls,
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `real.${scope}.called`,
          },
          datalabels: {
            align: 'start',
            anchor: 'start',
            offset: 20,
            display: false,
          },
        },
        {
          label: 'Appels prévisionnels',
          stack: 'callings',
          order: 4,
          backgroundColor: createDiagonalPattern(colors.calls),
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `provisional.${scope}.called`,
          },
          datalabels: {
            align: 'start',
            anchor: 'start',
            offset: 20,
            formatter: function (value) {
              const { called: realCalledAmount } = value.real[scope];
              const { called: provisionalCalledAmount } = value.provisional[scope];

              return numberToCurrency(+realCalledAmount + +provisionalCalledAmount, scope);
            },
            display: function (context) {
              const value = context.dataset.data[context.dataIndex];
              const { called: realAmount } = value.real[scope];
              const { called: provisionalAmount } = value.provisional[scope];
              const dataDisplayed = !context?.chart?.legend?.legendItems[0]?.hidden;

              return dataDisplayed && +realAmount + +provisionalAmount < 0;
            },
          },
        },
        {
          label: 'Distributions',
          stack: 'distributions',
          order: 2,
          backgroundColor: colors.distributions,
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `real.${scope}.distributed`,
          },
          datalabels: {
            align: 'end',
            anchor: 'end',
            display: function (context) {
              return false;
            },
          },
        },
        {
          label: 'Distributions prévisionnelles',
          stack: 'distributions',
          order: 5,
          backgroundColor: createDiagonalPattern(colors.distributions),
          data: sortedCashflows,
          parsing: {
            xAxisKey: 'year',
            yAxisKey: `provisional.${scope}.distributed`,
          },
          datalabels: {
            align: 'end',
            anchor: 'end',
            formatter: function (value) {
              const { distributed: realDistributedAmount } = value.real[scope];
              const { distributed: provisionalDistributedAmount } = value.provisional[scope];

              return numberToCurrency(+realDistributedAmount + +provisionalDistributedAmount, scope);
            },
            display: function (context) {
              const value = context.dataset.data[context.dataIndex];
              const { distributed: realAmount } = value.real[scope];
              const { distributed: provisionalAmount } = value.provisional[scope];

              const dataDisplayed = !context?.chart?.legend?.legendItems[1]?.hidden;

              return dataDisplayed && +realAmount + +provisionalAmount > 0;
            },
          },
        }
      ]
    }
  })

  const config = {
    type: 'bar',
    data: {
      labels: sortedCashflows.map((cashflow) => cashflow.year),
      datasets: buildDatasets(),
    },
    options: {
      devicePixelRatio: 1.5,
      aspectRatio: responsiveRatio,
      categoryPercentage: 0.5,
      barPercentage: 1,
      maxBarThickness: 100,
      responsive: true,
      scales: {
        x: {
          position: { y: 0 },
          ticks: {
            align: 'center',
            z: 10,
          },
          grid: defaultXAxisGridConfig,
        },
        y: {
          stacked: true,
          ticks: {
            display: false,
          },
          grid: {
            drawBorder: false,
            color: 'rgba(51,205,153,0.15)',
          },
          suggestedMin: -Math.abs(minYAxisValue),
          suggestedMax: Math.abs(maxYAxisValue),
        },
      },
      plugins: {
        legend: {
          position: 'bottom',
          align: 'center',
          labels: legendLabelsConfig,
        },
        tooltip: {
          callbacks: {
            label: tooltipBody,
          },
        },
      },
    },
  };

  return new Chart(chartCanvas, {
    plugins: [ChartDataLabels],
    ...config,
  });
};

function hasNonZeroDataForThisScope(annual_data, scope) {
  return annual_data.some((year_object) => {
    const hasNonZeroDataInReal = Object.values(year_object?.real[scope]).some(value => value !== 0);
    const hasNonZeroDataInProvisional = Object.values(year_object?.provisional[scope]).some(value => value !== 0);

    return hasNonZeroDataInReal || hasNonZeroDataInProvisional;
  })
}
